import React, { useMemo } from 'react';
import { Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { resolveEmbed, EMBED_REFUSAL, EMBED_SERVICE_LABELS } from './embedCatalog';
import { embedSourceKind, embedUrl } from './embedSource';
import { resolveEmbedSize, embedFrameStyle } from './embedLayout';
import { useConsent } from '../../../consent/useConsent';
import { BlockedEmbedPlaceholder } from '../../../consent/BlockedEmbedPlaceholder';
import classes from './EmbedBlock.module.css';

const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
const ALIGN_ITEMS = { start: 'flex-start', center: 'center', end: 'flex-end' };

const SERVICES_SENTENCE = `Services pris en charge : ${EMBED_SERVICE_LABELS.join(', ')}.`;

/** Une raison de refus → ce qui s'est passé et comment le corriger. */
function refusalMessage(refusal) {
  switch (refusal.reason) {
    case EMBED_REFUSAL.empty:
      return {
        title: 'Collez un lien à intégrer dans le panneau de droite',
        hint: SERVICES_SENTENCE,
      };
    case EMBED_REFUSAL.videoBlock:
      return {
        title: 'Les vidéos passent par le bloc Vidéo',
        hint: 'Remplacez ce bloc par un bloc Vidéo : il gère YouTube, Vimeo et les fichiers.',
        isError: true,
      };
    case EMBED_REFUSAL.invalidUrl:
      return {
        title: "Ce lien n'est pas une adresse https valide",
        hint: 'Collez l\'adresse complète de la page, en commençant par « https:// ».',
        isError: true,
      };
    case EMBED_REFUSAL.unsupportedPath:
      return {
        title: `Ce lien ${refusal.label} ne pointe pas vers un contenu intégrable`,
        hint: `Ouvrez le contenu chez ${refusal.label} et copiez le lien de partage.`,
        isError: true,
      };
    case EMBED_REFUSAL.shortLink:
      return {
        title: 'Ce lien raccourci ne peut pas être intégré',
        hint: `${refusal.label} est bien pris en charge, mais un lien court ne dit pas ce qu'il désigne. `
          + 'Ouvrez-le, puis « Partager → Intégrer une carte » et collez ici le contenu HTML copié — '
          + "ou copiez simplement l'adresse complète depuis la barre du navigateur.",
        isError: true,
      };
    default:
      return {
        title: "Ce service n'est pas pris en charge",
        hint: SERVICES_SENTENCE,
        isError: true,
      };
  }
}

/**
 * Bloc Puck — Contenu externe (Calendly, Loom, Figma, Slides, Maps, formulaires).
 *
 * L'intégration se voit telle quelle dans l'éditeur, mais sans recevoir les
 * clics (`pointer-events: none`, comme le bloc Vidéo) : un iframe interactif
 * avalerait la sélection et le glisser-déposer du bloc. `loading="lazy"` évite
 * qu'une page pleine d'embeds charge tous les tiers d'un coup.
 * Une URL non résolue affiche une carte d'explication en édition, et rien du
 * tout sur le site — jamais de message au lecteur.
 */
const EmbedBlock = (props) => {
  const {
    id,
    sizeMode = 'auto',
    aspectRatio = '16/9',
    height = '',
    width,
    alignment = 'center',
    /* 'start' et non 'center' : sans réglage, le bloc reste posé en haut de sa
       colonne, comme avant que le champ existe. */
    alignVertical = 'start',
    puck,
  } = props;
  const isEditing = !!puck?.isEditing;
  const { enforced, isServiceAllowed } = useConsent();

  /* L'adresse peut venir d'une variable : sa valeur est copiée dans les props,
     rien n'est résolu à l'affichage. Vide sur un modèle de site, où le
     catalogue n'a que des noms — la carte d'explication le dit. */
  const url = embedUrl(props);

  // Calendly et Figma veulent connaître le domaine qui les intègre (redimension-
  // nement, analytics). Le catalogue garde un défaut pour rester pur.
  const embed = useMemo(() => resolveEmbed(url, {
    embedHost: typeof window === 'undefined' ? undefined : window.location.hostname,
  }), [url]);
  const size = resolveEmbedSize({ sizeMode, aspectRatio, height }, embed.defaultSize);
  const frameStyle = embedFrameStyle(size);

  const wrap = (children) => (
    /* `data-box-align` : la colonne étire la box, ce qui donne de la hauteur à
       son `align-items` (ProposalSection.module.css). */
    <div
      id={id}
      data-box-align=""
      className={classes.row}
      style={{
        justifyContent: JUSTIFY[alignment] || 'center',
        alignItems: ALIGN_ITEMS[alignVertical] || 'flex-start',
      }}
    >
      <div className={classes.box} style={{ maxWidth: width ? `${width}px` : undefined }}>
        {children}
      </div>
    </div>
  );

  if (!embed.ok) {
    if (!isEditing) return null;
    const { title, hint, isError } = embedSourceKind(props) === 'variable' && !url
      ? {
        title: props.variableName
          ? `La valeur de « ${props.variableName} » sera lue à la génération du site`
          : 'Choisissez une variable dans le panneau de droite',
        hint: SERVICES_SENTENCE,
      }
      : refusalMessage(embed);
    const plateIcon = isError
      ? icon({ name: 'triangle-exclamation', style: 'light' })
      : icon({ name: 'browser', style: 'light' });
    return wrap(
      <div
        className={`${classes.card} ${isError ? classes.cardError : ''}`.trim()}
        style={frameStyle}
      >
        <div className={classes.plate}>
          <span className={classes.plateIcon}>
            <FontAwesomeIcon icon={plateIcon} style={{ fontSize: 24 }} aria-hidden="true" />
          </span>
          <Text size="sm" fw={500}>{title}</Text>
          <Text className={classes.services}>{hint}</Text>
        </div>
      </div>,
    );
  }

  // Second et dernier point d'étranglement du montage d'iframe (avec
  // `media/MediaEmbed.jsx`). En édition, `enforced` est faux : l'intégration
  // s'affiche comme avant.
  if (enforced && !isServiceAllowed(embed.serviceId)) {
    return wrap(<BlockedEmbedPlaceholder serviceId={embed.serviceId} style={frameStyle} />);
  }

  return wrap(
    <iframe
      src={embed.iframeSrc}
      title={`${embed.label} — contenu intégré`}
      className={classes.frame}
      style={{ ...frameStyle, ...(isEditing ? { pointerEvents: 'none' } : null) }}
      sandbox={embed.sandbox}
      loading="lazy"
      allowFullScreen
    />,
  );
};

export default EmbedBlock;
